import React, { useState, useEffect, useCallback, useRef, lazy, Suspense } from 'react';
import { useParams, Link } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useWS } from '../hooks/useWebSocket';
import { timeAgo, formatDateTime, formatTime, formatDate } from '../utils/dateFormat';

const MapView = lazy(() => import('../components/MapView'));

function Section({ title, actions, children }) {
  return (
    <div className="bg-white rounded-xl shadow-card">
      <div className="px-5 py-4 border-b border-secondary-100 flex items-center justify-between">
        <h3 className="font-semibold text-secondary-900">{title}</h3>
        {actions && <div className="flex gap-2">{actions}</div>}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <p className="text-xs text-secondary-400 mb-0.5">{label}</p>
      <p className="text-sm text-secondary-800 font-medium">{value || '--'}</p>
    </div>
  );
}

function ActionBtn({ onClick, color = 'primary', loading, children }) {
  const colors = {
    primary: 'bg-primary-50 text-primary-700 hover:bg-primary-100',
    red: 'bg-red-50 text-red-700 hover:bg-red-100',
    amber: 'bg-amber-50 text-amber-700 hover:bg-amber-100',
    blue: 'bg-blue-50 text-blue-700 hover:bg-blue-100',
  };
  return (
    <button onClick={onClick} disabled={loading}
      className={`px-3 py-1.5 text-xs font-medium rounded-lg transition disabled:opacity-50 ${colors[color]}`}>
      {children}
    </button>
  );
}

// LED state visual mapping — mirrors firmware ButtonManager states.
// Modern firmware reports the patient-state name (ok, checkin_window, …,
// not_configured) in led_state; the legacy color keys are kept for older
// builds that reported a raw color.
const LED_STATES = {
  default:     { label: 'Default',      color: 'bg-secondary-300',  ring: 'ring-secondary-200' },
  // Firmware patient states (ButtonManager::patientStateName)
  ok:               { label: 'OK',                color: 'bg-emerald-500', ring: 'ring-emerald-300' },
  checkin_window:   { label: 'Check-in Window',   color: 'bg-amber-400',   ring: 'ring-amber-300' },
  checkin_required: { label: 'Check-in Required', color: 'bg-red-500',     ring: 'ring-red-300', animate: true },
  degraded:         { label: 'Degraded',          color: 'bg-orange-500',  ring: 'ring-orange-300', animate: true },
  help_called:      { label: 'Help Called',       color: 'bg-red-700',     ring: 'ring-red-400', animate: true },
  vacation:         { label: 'Vacation',          color: 'bg-blue-400',    ring: 'ring-blue-300' },
  fallback_setup:   { label: 'Setup Mode',        color: 'bg-cyan-500',    ring: 'ring-cyan-300' },
  not_configured:   { label: 'Not Configured',    color: 'bg-orange-400',  ring: 'ring-orange-300', animate: true },
  // Legacy raw-color keys (pre-patient-state firmware)
  white:       { label: 'White',        color: 'bg-white border border-secondary-300', ring: 'ring-secondary-200' },
  off:         { label: 'Off',          color: 'bg-secondary-800',  ring: 'ring-secondary-400' },
  blue:        { label: 'Blue',         color: 'bg-blue-500',       ring: 'ring-blue-300' },
  flashingRed: { label: 'Flashing Red', color: 'bg-red-500',        ring: 'ring-red-300', animate: true },
  rapidRed:    { label: 'Rapid Red',    color: 'bg-red-600',        ring: 'ring-red-400', animate: true },
  solidRed:    { label: 'Solid Red',    color: 'bg-red-700',        ring: 'ring-red-400' },
};

function LEDStateField({ state }) {
  const led = LED_STATES[state] || LED_STATES.default;
  return (
    <div>
      <p className="text-xs text-secondary-400 mb-0.5">LED State</p>
      <div className="flex items-center gap-2">
        <span
          className={`inline-block w-3.5 h-3.5 rounded-full ${led.color} ring-2 ${led.ring} ${led.animate ? 'animate-pulse' : ''}`}
        />
        <span className="text-sm text-secondary-800 font-medium">{led.label}</span>
      </div>
    </div>
  );
}

export default function DeviceDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [device, setDevice] = useState(null);
  const [config, setConfig] = useState(null);
  const [checkinStatus, setCIStatus] = useState(null);
  const [telemetry, setTelemetry] = useState([]);
  const [telemetryStats, setTelemetryStats] = useState(null);
  const [buttonLog, setButtonLog] = useState([]);
  const [configHistory, setConfigHistory] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState('');
  const [editingConfig, setEditingConfig] = useState(false);
  const [configForm, setConfigForm] = useState({});
  const [activeTab, setActiveTab] = useState('info');
  // Map source for the location panel: device-reported vs registered address vs both.
  const [mapSource, setMapSource] = useState('device');

  // Firmware tab state
  const [boardFirmware, setBoardFirmware] = useState([]);
  const [deviceRollouts, setDeviceRollouts] = useState([]);
  const [selectedFwId, setSelectedFwId] = useState('');
  const [fwActionMsg, setFwActionMsg] = useState('');
  const [fwActionErr, setFwActionErr] = useState('');

  // Audio Message tab state
  const [messageText, setMessageText] = useState('');
  const [messageSending, setMessageSending] = useState(false);
  const [activeMessageId, setActiveMessageId] = useState(null);
  const [messageStatus, setMessageStatus] = useState(null); // queued | playing | cleared | null
  const [messageErr, setMessageErr] = useState('');
  const [messageInfo, setMessageInfo] = useState('');

  const fetchDevice = useCallback(async () => {
    setLoading(true);
    try {
      const devRes = await api.get(`/devices/${id}`);
      const dev = devRes.data.device;
      setDevice(dev);

      const results = await Promise.allSettled([
        api.get(`/config/${dev.device_id}`),
        api.get(`/config/${dev.device_id}/checkin-status`),
        api.get(`/telemetry/${dev.device_id}?limit=100`),
        api.get(`/telemetry/${dev.device_id}/stats?days=7`),
        api.get(`/devices/${dev.device_id}/button-log?limit=20`),
        api.get(`/config/${dev.device_id}/history?limit=10`),
        api.get(`/vacation/${dev.device_id}`),
        api.get(`/subscriptions/${dev.device_id}`),
      ]);

      if (results[0].status === 'fulfilled') setConfig(results[0].value.data.config);
      if (results[1].status === 'fulfilled') setCIStatus(results[1].value.data);
      if (results[2].status === 'fulfilled') setTelemetry(results[2].value.data.data || []);
      if (results[3].status === 'fulfilled') setTelemetryStats(results[3].value.data.stats);
      if (results[4].status === 'fulfilled') setButtonLog(results[4].value.data.data || []);
      if (results[5].status === 'fulfilled') setConfigHistory(results[5].value.data.data || []);
      if (results[7].status === 'fulfilled') setSubscription(results[7].value.data.subscription);
    } catch {} finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchDevice(); }, [fetchDevice]);

  // Firmware tab data — load builds for this device's board model + rollout history
  const fetchFirmwareForDevice = useCallback(async () => {
    if (!device?.device_id) return;
    const board = device.board_model || 'v3';
    try {
      const [fwRes, roRes] = await Promise.allSettled([
        api.get(`/ota/firmware?board_model=${encodeURIComponent(board)}`),
        api.get(`/ota/rollouts?device_id=${encodeURIComponent(device.device_id)}`),
      ]);
      if (fwRes.status === 'fulfilled') {
        setBoardFirmware(Array.isArray(fwRes.value.data.data) ? fwRes.value.data.data : []);
      }
      if (roRes.status === 'fulfilled') {
        setDeviceRollouts(Array.isArray(roRes.value.data.data) ? roRes.value.data.data : []);
      }
    } catch {}
  }, [device?.device_id, device?.board_model]);

  useEffect(() => {
    if (activeTab === 'firmware') fetchFirmwareForDevice();
  }, [activeTab, fetchFirmwareForDevice]);

  const assignFirmwareToDevice = async () => {
    setFwActionMsg(''); setFwActionErr('');
    if (!selectedFwId) { setFwActionErr('Select a firmware build first.'); return; }
    setActionLoading('fw-assign');
    try {
      const { data } = await api.post('/ota/assign', {
        device_id: device.device_id,
        firmware_version_id: Number(selectedFwId),
      });
      setFwActionMsg(`Queued ${data.targetVersion || ''} for ${device.device_id}.`);
      setSelectedFwId('');
      await fetchDevice();
      await fetchFirmwareForDevice();
    } catch (e) {
      setFwActionErr(e.response?.data?.error || e.message || 'Assign failed');
    } finally {
      setActionLoading('');
    }
  };

  const cancelFirmwareForDevice = async () => {
    setFwActionMsg(''); setFwActionErr('');
    setActionLoading('fw-cancel');
    try {
      await api.post('/ota/cancel', { device_id: device.device_id });
      setFwActionMsg('Firmware target cleared.');
      await fetchDevice();
      await fetchFirmwareForDevice();
    } catch (e) {
      setFwActionErr(e.response?.data?.error || 'Cancel failed');
    } finally {
      setActionLoading('');
    }
  };

  const revertFirmwareForDevice = async () => {
    setFwActionMsg(''); setFwActionErr('');
    if (!window.confirm(`Revert ${device.device_id} to ${device.previous_firmware_version || 'previous version'}?`)) return;
    setActionLoading('fw-revert');
    try {
      const { data } = await api.post('/ota/revert', { device_id: device.device_id });
      setFwActionMsg(`Reverting to ${data.revertTo || 'previous version'}.`);
      await fetchDevice();
      await fetchFirmwareForDevice();
    } catch (e) {
      setFwActionErr(e.response?.data?.error || 'Revert failed');
    } finally {
      setActionLoading('');
    }
  };

  const doAction = async (name, fn) => {
    setActionLoading(name);
    try { await fn(); await fetchDevice(); } catch (e) { alert(e.response?.data?.error || 'Action failed'); }
    finally { setActionLoading(''); }
  };

  const toggleVacation = () => doAction('vacation', () =>
    api.post(`/vacation/${device.device_id}/toggle`, { active: !device.vacation_mode, notes: 'Toggled from dashboard' })
  );
  const toggleMute = () => doAction('mute', () =>
    api.post(`/config/${device.device_id}/mute-notifications`, { muted: !device.notifications_muted })
  );
  const queueReboot = () => { if (window.confirm('Queue a reboot for this device?')) doAction('reboot', () => api.post(`/config/${device.device_id}/reboot`)); };
  const factoryReset = () => { if (window.confirm('FACTORY RESET this device? This will clear all config, unlink patient, and resolve alerts.')) doAction('reset', () => api.post(`/config/${device.device_id}/factory-reset`)); };
  const resetCheckin = () => doAction('checkin', () => api.post(`/config/${device.device_id}/reset-checkin`));
  const purgeDevice = () => {
    const answer = window.prompt(
      `⚠️ PURGE ${device.device_id}?\n\nThis IRREVERSIBLY deletes ALL telemetry, check-in, button, config, and OTA history, clears the firmware target, and resets the record to brand-new (the device identity is kept so it can re-onboard).\n\nType PURGE to confirm.`
    );
    if (answer !== 'PURGE') return;
    doAction('purge', () => api.post(`/devices/${device.device_id}/purge`));
  };

  const claimDevice = async () => {
    if (!window.confirm('Claim this device to your account? You will become its owner.')) return;
    setActionLoading('claim');
    try {
      await api.post(`/provisioning/${device.device_id}/claim-ownership`, { method: 'manual' });
      await fetchDevice();
    } catch (e) { alert(e.response?.data?.error || 'Claim failed'); }
    finally { setActionLoading(''); }
  };

  const startEditConfig = () => {
    setConfigForm({
      patient_name: config?.patient_name || '',
      patient_age: config?.patient_age || '',
      patient_address: config?.patient_address || '',
      patient_notes: config?.patient_notes || '',
      caregiver_email: config?.caregiver_email || '',
      caregiver_phone: config?.caregiver_phone || '',
      timezone: config?.timezone || 'America/Chicago',
      sound_enabled: config?.sound_enabled !== false,   // default: sound on
      volume_level: config?.volume_level || 'max',
    });
    setEditingConfig(true);
  };

  const saveConfig = async () => {
    setActionLoading('config');
    try {
      await api.post(`/config/${device.device_id}/publish`, configForm);
      setEditingConfig(false);
      await fetchDevice();
    } catch (e) { alert(e.response?.data?.error || 'Save failed'); }
    finally { setActionLoading(''); }
  };

  // ── Audio Message (caregiver custom message) ────────────────────────────────
  const sendCustomMessage = async () => {
    const text = messageText.trim();
    if (!text) return;
    setMessageSending(true); setMessageErr(''); setMessageInfo('');
    try {
      const res = await api.post(`/devices/${device.device_id}/message`, { type: 'tts', text });
      setActiveMessageId(res.data.message_id ?? null);
      setMessageStatus('queued');
      setMessageText('');
      setMessageInfo('Queued — the device downloads and plays it on its next heartbeat.');
    } catch (e) {
      setMessageErr(e.response?.status === 404
        ? 'Audio messaging is not enabled on this server.'
        : (e.response?.data?.error || 'Failed to send message'));
    } finally { setMessageSending(false); }
  };

  const clearCustomMessage = async () => {
    if (!activeMessageId) return;
    setMessageSending(true); setMessageErr(''); setMessageInfo('');
    try {
      await api.delete(`/devices/${device.device_id}/message/${activeMessageId}`);
      setMessageStatus('cleared');
      setMessageInfo('Stop sent — the device clears the message on its next heartbeat.');
    } catch (e) {
      if (e.response?.status === 409) { setMessageStatus('cleared'); setMessageInfo('Message was already cleared.'); }
      else { setMessageErr(e.response?.data?.error || 'Failed to stop message'); }
    } finally { setMessageSending(false); }
  };

  // Load the currently-active message when the tab opens so status + the Stop
  // button survive a page reload (there's no live state otherwise until a WS event).
  const fetchActiveMessage = useCallback(async () => {
    if (!device?.device_id) return;
    try {
      const res = await api.get(`/devices/${device.device_id}/message`);
      const m = res.data.message;
      if (m) {
        setActiveMessageId(m.message_id);
        setMessageStatus(m.status === 'delivered' ? 'playing' : 'queued');
      }
    } catch (e) { /* 404 = feature disabled; leave the composer usable */ }
  }, [device]);
  useEffect(() => { if (activeTab === 'message') fetchActiveMessage(); }, [activeTab, fetchActiveMessage]);

  // Ref mirror of the active id so the WS handler can correlate events without
  // re-subscribing on every id change (the handler closes over [device] only).
  const activeMessageIdRef = useRef(null);
  useEffect(() => { activeMessageIdRef.current = activeMessageId; }, [activeMessageId]);

  // Live delivered/cleared events from the device (scoped to this device).
  useWS(['message:delivered', 'message:cleared'], useCallback((type, data) => {
    if (!device || data?.deviceId !== device.device_id) return;
    if (type === 'message:delivered') {
      setActiveMessageId(data.messageId);
      setMessageStatus('playing');
    } else if (type === 'message:cleared') {
      // Only reflect a clear for the message we're currently tracking, so a late
      // event for a superseded/old id can't clobber a newer message's state.
      if (activeMessageIdRef.current == null || data.messageId === activeMessageIdRef.current) {
        setMessageStatus('cleared');
      }
    }
  }, [device]));

  if (loading) return <div className="flex items-center justify-center h-64 text-secondary-400">Loading...</div>;
  if (!device) return <div className="text-red-500">Device not found</div>;

  const chartData = [...telemetry].reverse().map(t => {
    const extra = t.additional_data ? (typeof t.additional_data === 'string' ? JSON.parse(t.additional_data) : t.additional_data) : {};
    return {
      time: new Date(t.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      signal: t.wifi_signal_strength,
      uptime: t.uptime_seconds ? Math.round(t.uptime_seconds / 3600 * 10) / 10 : null,
      power_source: t.power_source,
      battery_level: extra.battery_level ?? null,
      config_version: t.config_version,
      vacation_mode: t.vacation_mode,
    };
  });

  const tabs = [
    { id: 'info', label: 'Info' },
    { id: 'config', label: 'Config' },
    { id: 'message', label: 'Audio Message' },
    { id: 'firmware', label: 'Firmware' },
    { id: 'checkins', label: 'Check-ins' },
    { id: 'telemetry', label: 'Telemetry' },
    { id: 'buttons', label: 'Buttons' },
    { id: 'history', label: 'Config History' },
  ];

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Link to="/devices" className="text-secondary-400 hover:text-secondary-600">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">{device.device_id}</h1>
          {device.location && <p className="text-sm text-secondary-500">{device.location}</p>}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
            device.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
          }`}>{device.status}</span>
          {device.vacation_mode && <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700">Vacation</span>}
          {subscription && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-50 text-purple-700">{subscription.plan}</span>}
        </div>
      </div>

      {/* Action bar */}
      <div className="flex flex-wrap gap-2">
        <ActionBtn onClick={toggleVacation} color="blue" loading={actionLoading === 'vacation'}>
          {device.vacation_mode ? 'Disable Vacation' : 'Enable Vacation'}
        </ActionBtn>
        <ActionBtn onClick={toggleMute} color="amber" loading={actionLoading === 'mute'}>
          {device.notifications_muted ? 'Unmute Notifications' : 'Mute Notifications'}
        </ActionBtn>
        <ActionBtn onClick={resetCheckin} color="primary" loading={actionLoading === 'checkin'}>Reset Check-in</ActionBtn>
        <ActionBtn onClick={queueReboot} color="amber" loading={actionLoading === 'reboot'}>Reboot</ActionBtn>
        <ActionBtn onClick={factoryReset} color="red" loading={actionLoading === 'reset'}>Factory Reset</ActionBtn>
        {user?.role === 'super_admin' && (
          <ActionBtn onClick={purgeDevice} color="red" loading={actionLoading === 'purge'}>Purge (Full Reset)</ActionBtn>
        )}
      </div>

      {/* Claim banner — shown for unclaimed devices */}
      {!device.owner_id && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-5 py-4 flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-amber-900">This device is not linked to any account</p>
            <p className="text-xs text-amber-700 mt-0.5">
              It self-registered via firmware but has no owner yet. Claim it to add it to your Registered devices.
            </p>
          </div>
          <button onClick={claimDevice} disabled={actionLoading === 'claim'}
            className="ml-4 px-4 py-2 bg-amber-600 text-white text-sm font-medium rounded-lg hover:bg-amber-700 transition disabled:opacity-50 whitespace-nowrap">
            {actionLoading === 'claim' ? 'Claiming...' : 'Claim This Device'}
          </button>
        </div>
      )}

      {/* Location-discrepancy warning — device reports far from the patient's address */}
      {device.locationCheck?.mismatch && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-5 py-4 flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M5.07 19h13.86a2 2 0 001.74-2.99l-6.93-12a2 2 0 00-3.48 0l-6.93 12A2 2 0 005.07 19z" />
            </svg>
            <div>
              <p className="text-sm font-semibold text-red-900">
                Device location doesn't match the patient's address
              </p>
              <p className="text-xs text-red-700 mt-0.5">
                The device reports a location about{' '}
                <strong>{(device.locationCheck.distanceMeters / 1609.34).toFixed(1)} mi</strong>{' '}
                from the registered address{device.locationCheck.address?.label ? ` (${device.locationCheck.address.label})` : ''}.
                Verify the device is at the correct location.
              </p>
            </div>
          </div>
          <button
            onClick={() => { setActiveTab('telemetry'); setMapSource('both'); }}
            className="px-3 py-1.5 bg-red-600 text-white text-xs font-medium rounded-lg hover:bg-red-700 transition whitespace-nowrap"
          >
            Compare on map
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-secondary-200">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition ${
              activeTab === t.id ? 'border-primary-600 text-primary-700' : 'border-transparent text-secondary-500 hover:text-secondary-700'
            }`}>{t.label}</button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'info' && (
        <div className="grid lg:grid-cols-2 gap-5">
          <Section title="Device Info">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Status" value={device.status} />
              <LEDStateField state={device.led_state} />
              <Field label="Last Seen" value={timeAgo(device.last_seen_at)} />
              <Field label="Board Model" value={(device.board_model || 'v3').toUpperCase()} />
              <Field label="Firmware" value={device.firmware_version} />
              <Field label="Vacation Mode" value={device.vacation_mode ? 'Active' : 'Off'} />
              <Field label="Notifications" value={device.notifications_muted ? 'Muted' : 'Active'} />
              <Field label="Config Version" value={device.config_version || '--'} />
              <Field label="Provisioning" value={(device.provisioning_status || 'unregistered').replace(/_/g, ' ')} />
              <Field label="Last Button Press" value={device.last_button_press_at ? formatDateTime(device.last_button_press_at) : '--'} />
              <Field label="Signal Strength" value={device.wifi_signal_strength ? `${device.wifi_signal_strength} dBm` : '--'} />
              <Field label="Last Known Position" value={
                device.latitude && device.longitude
                  ? `${Number(device.latitude).toFixed(5)}, ${Number(device.longitude).toFixed(5)}`
                  : '--'
              } />
            </div>
          </Section>
          {telemetryStats && (
            <Section title="7-Day Telemetry Summary">
              <div className="grid grid-cols-2 gap-4">
                <Field label="Avg WiFi Signal" value={`${telemetryStats.avg_signal ?? '--'} dBm`} />
                <Field label="Uptime" value={`${telemetryStats.uptimePercent ?? '--'}%`} />
                <Field label="Total Records" value={telemetryStats.total_records} />
                <Field label="Period" value={`${telemetryStats.periodDays} days`} />
              </div>
            </Section>
          )}
        </div>
      )}

      {activeTab === 'config' && (
        <Section title="Patient & Config" actions={
          !editingConfig ? <ActionBtn onClick={startEditConfig}>Edit</ActionBtn> : null
        }>
          {editingConfig ? (
            <div className="space-y-4">
              {['patient_name', 'patient_age', 'patient_address', 'patient_notes', 'caregiver_email', 'caregiver_phone'].map(field => (
                <div key={field}>
                  <label className="block text-xs text-secondary-500 mb-1">{field.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</label>
                  {field === 'patient_notes' ? (
                    <textarea value={configForm[field] || ''} onChange={e => setConfigForm(f => ({ ...f, [field]: e.target.value }))}
                      className="w-full px-3 py-2 border border-secondary-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none" rows={3} />
                  ) : (
                    <input type={field.includes('email') ? 'email' : 'text'} value={configForm[field] || ''}
                      onChange={e => setConfigForm(f => ({ ...f, [field]: e.target.value }))}
                      className="w-full px-3 py-2 border border-secondary-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none" />
                  )}
                </div>
              ))}
              <div>
                <label className="block text-xs text-secondary-500 mb-1">Timezone</label>
                <select value={configForm.timezone} onChange={e => setConfigForm(f => ({ ...f, timezone: e.target.value }))}
                  className="w-full px-3 py-2 border border-secondary-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none">
                  {['America/New_York','America/Chicago','America/Denver','America/Phoenix','America/Los_Angeles','America/Anchorage','Pacific/Honolulu'].map(tz => (
                    <option key={tz} value={tz}>{tz}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-secondary-500 mb-1">Volume</label>
                <select value={configForm.volume_level} onChange={e => setConfigForm(f => ({ ...f, volume_level: e.target.value }))}
                  className="w-full px-3 py-2 border border-secondary-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none">
                  {['min', 'medium', 'max'].map(v => (<option key={v} value={v}>{v}</option>))}
                </select>
              </div>
              <label className="flex items-center gap-2 text-sm text-secondary-700">
                <input type="checkbox" checked={configForm.sound_enabled !== false}
                  onChange={e => setConfigForm(f => ({ ...f, sound_enabled: e.target.checked }))}
                  className="rounded border-secondary-300" />
                Sounds enabled <span className="text-xs text-secondary-400">(off mutes routine reminders; the emergency alert always plays)</span>
              </label>
              <div className="flex gap-2 pt-2">
                <button onClick={saveConfig} disabled={actionLoading === 'config'}
                  className="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50">
                  {actionLoading === 'config' ? 'Saving...' : 'Publish Config'}
                </button>
                <button onClick={() => setEditingConfig(false)} className="px-4 py-2 text-sm text-secondary-600 hover:text-secondary-800">Cancel</button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              <Field label="Patient" value={config?.patient_name} />
              <Field label="Age" value={config?.patient_age} />
              <Field label="Address" value={config?.patient_address} />
              <Field label="Notes" value={config?.patient_notes} />
              <Field label="Caregiver Email" value={config?.caregiver_email} />
              <Field label="Caregiver Phone" value={config?.caregiver_phone} />
              <Field label="Timezone" value={config?.timezone} />
              <Field label="Heartbeat Interval" value={config?.heartbeat_interval_seconds ? `${config.heartbeat_interval_seconds}s` : '--'} />
              <Field label="Emergency Threshold" value={config?.emergency_threshold} />
              <Field label="Sound" value={config?.sound_enabled === false ? 'Off (muted)' : 'On'} />
              <Field label="Volume" value={config?.volume_level || 'max'} />
            </div>
          )}
        </Section>
      )}

      {activeTab === 'firmware' && (() => {
        // OTA only advances when the device heartbeats (every 15s in first 60 min
        // after boot, every 5 min after). If last_seen_at is stale while an OTA
        // is queued, the device won't pick it up until it calls home again.
        const lastSeenMs = device.last_seen_at ? new Date(device.last_seen_at).getTime() : 0;
        const heartbeatAgeMs = lastSeenMs ? Date.now() - lastSeenMs : null;
        const staleHeartbeat = heartbeatAgeMs === null || heartbeatAgeMs > 10 * 60 * 1000;
        const otaQueued = device.ota_status === 'pending' || device.ota_status === 'notified';
        return (
        <div className="grid lg:grid-cols-2 gap-5">
          <Section title="Firmware State">
            {otaQueued && staleHeartbeat && (
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800 font-medium mb-1">
                  OTA queued but device hasn't checked in {heartbeatAgeMs === null ? 'yet' : `for ${timeAgo(device.last_seen_at)}`}
                </p>
                <p className="text-xs text-amber-700">
                  The device pulls firmware on its next heartbeat (every 15s in first 60 min after boot, every 5 min after).
                  If it stays offline, power-cycle it and watch the serial log for <span className="font-mono">[SMC] POST /heartbeat -&gt; HTTP 200</span>.
                </p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <Field label="Board Model" value={(device.board_model || 'v3').toUpperCase()} />
              <Field label="Current Version" value={device.firmware_version} />
              <Field label="Target Version" value={device.target_firmware_version || '--'} />
              <Field label="Previous Version" value={device.previous_firmware_version || '--'} />
              <Field label="OTA Status" value={device.ota_status || 'idle'} />
              <Field label="Last OTA Update" value={device.ota_updated_at ? formatDateTime(device.ota_updated_at) : '--'} />
              <Field
                label="Last Heartbeat"
                value={device.last_seen_at ? `${timeAgo(device.last_seen_at)} (${formatDateTime(device.last_seen_at)})` : 'Never'}
              />
            </div>
            {device.ota_error && (
              <div className="mt-4 p-3 bg-red-50 border border-red-100 rounded-lg">
                <p className="text-xs text-red-700 font-medium mb-1">Last OTA Error</p>
                <p className="text-xs text-red-600 font-mono">{device.ota_error}</p>
              </div>
            )}
          </Section>

          <Section title="Set Firmware">
            <div className="space-y-3">
              <p className="text-xs text-secondary-500">
                Showing builds for board <span className="font-semibold uppercase">{device.board_model || 'v3'}</span>.
                Cross-board pushes are blocked by the server.
              </p>
              <select
                value={selectedFwId}
                onChange={(e) => setSelectedFwId(e.target.value)}
                className="w-full px-3 py-2 border border-secondary-200 rounded-lg text-sm text-secondary-700 focus:ring-2 focus:ring-primary-500 outline-none"
              >
                <option value="">Select firmware build...</option>
                {boardFirmware.map(fw => (
                  <option key={fw.id} value={fw.id}>
                    {fw.version}{fw.variant_label ? ` (${fw.variant_label})` : ''} — {formatDate(fw.release_date)}
                  </option>
                ))}
              </select>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={assignFirmwareToDevice}
                  disabled={actionLoading === 'fw-assign' || !selectedFwId}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg bg-primary-50 text-primary-700 hover:bg-primary-100 disabled:opacity-50"
                >
                  {actionLoading === 'fw-assign' ? 'Queuing...' : 'Push to this device'}
                </button>
                {(device.ota_status === 'pending' || device.target_firmware_version) && (
                  <button
                    onClick={cancelFirmwareForDevice}
                    disabled={actionLoading === 'fw-cancel'}
                    className="px-3 py-1.5 text-xs font-medium rounded-lg bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-50"
                  >
                    {device.ota_status === 'pending' ? 'Cancel pending OTA' : 'Clear firmware target'}
                  </button>
                )}
                {device.previous_firmware_version && device.previous_firmware_version !== device.firmware_version && (
                  <button
                    onClick={revertFirmwareForDevice}
                    disabled={actionLoading === 'fw-revert'}
                    className="px-3 py-1.5 text-xs font-medium rounded-lg bg-orange-50 text-orange-700 hover:bg-orange-100 disabled:opacity-50"
                  >
                    Revert to {device.previous_firmware_version}
                  </button>
                )}
              </div>
              {fwActionMsg && <p className="text-xs text-emerald-600">{fwActionMsg}</p>}
              {fwActionErr && <p className="text-xs text-red-600">{fwActionErr}</p>}
            </div>
          </Section>

          <div className="lg:col-span-2">
            <Section title="OTA History">
              {deviceRollouts.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-secondary-50 text-secondary-600">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium">Target</th>
                        <th className="px-4 py-2 text-left font-medium">Status</th>
                        <th className="px-4 py-2 text-left font-medium">Assigned</th>
                        <th className="px-4 py-2 text-left font-medium">Started</th>
                        <th className="px-4 py-2 text-left font-medium">Completed</th>
                        <th className="px-4 py-2 text-left font-medium">By</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-secondary-100">
                      {deviceRollouts.map(r => (
                        <tr key={r.id} className="hover:bg-secondary-50">
                          <td className="px-4 py-2 font-mono text-xs">{r.firmware_version}</td>
                          <td className="px-4 py-2 text-xs">
                            <span className={`px-2 py-0.5 rounded-full font-medium ${
                              r.status === 'success' || r.status === 'completed' ? 'bg-emerald-50 text-emerald-700'
                                : r.status === 'failed' ? 'bg-red-50 text-red-700'
                                : r.status === 'cancelled' ? 'bg-secondary-100 text-secondary-500'
                                : 'bg-amber-50 text-amber-700'
                            }`}>{r.status}</span>
                          </td>
                          <td className="px-4 py-2 text-xs">{r.assigned_at ? formatDateTime(r.assigned_at) : '--'}</td>
                          <td className="px-4 py-2 text-xs">{r.started_at ? formatDateTime(r.started_at) : '--'}</td>
                          <td className="px-4 py-2 text-xs">{r.completed_at ? formatDateTime(r.completed_at) : '--'}</td>
                          <td className="px-4 py-2 text-xs">{r.assigned_by_email || r.assigned_by || '--'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : <p className="text-sm text-secondary-400">No OTA history for this device</p>}
            </Section>
          </div>
        </div>
        );
      })()}

      {activeTab === 'checkins' && (
        <div className="grid lg:grid-cols-2 gap-5">
          <Section title="Check-in Schedule">
            {config?.schedule?.length > 0 ? (
              <div className="space-y-2">
                {config.schedule.map(s => (
                  <div key={s.slot} className={`flex items-center justify-between py-2 px-3 rounded-lg ${s.enabled ? 'bg-secondary-50' : 'bg-secondary-50/50 opacity-50'}`}>
                    <div>
                      <span className="text-sm font-medium text-secondary-800">{s.label || `Slot ${s.slot}`}</span>
                      <span className="text-xs text-secondary-400 ml-2">{s.time}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {!s.enabled && <span className="text-xs text-secondary-400">Disabled</span>}
                      {s.medicine && <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">Medicine</span>}
                      <span className="text-xs text-secondary-400">{s.window_minutes}m</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-secondary-400">No schedule configured</p>}
          </Section>
          <Section title="Today's Check-ins" actions={<ActionBtn onClick={resetCheckin} loading={actionLoading === 'checkin'}>Reset</ActionBtn>}>
            {checkinStatus?.slots?.length > 0 ? (
              <div className="space-y-2">
                {checkinStatus.slots.map((s, i) => (
                  <div key={i} className="flex items-center justify-between py-2 px-3 bg-secondary-50 rounded-lg">
                    <span className="text-sm text-secondary-700">{s.label || `Slot ${s.slot_number}`} ({s.checkin_time})</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      s.status === 'completed' ? 'bg-emerald-50 text-emerald-700'
                        : s.status === 'missed' ? 'bg-red-50 text-red-700'
                        : s.status === 'pending' ? 'bg-amber-50 text-amber-700'
                        : 'bg-secondary-100 text-secondary-500'
                    }`}>{s.status}</span>
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-secondary-400">No check-in status available</p>}
          </Section>
        </div>
      )}

      {activeTab === 'telemetry' && (
        <div className="space-y-5">
          {device.latitude && device.longitude && (() => {
            const lc = device.locationCheck;
            const addr = lc?.address;
            const addrMarker = addr ? {
              id: null,
              device_id: 'Registered Address',
              config_patient_name: addr.label || 'Registered address',
              latitude: addr.lat, longitude: addr.lng,
              status: 'maintenance', location_accuracy: 'manual',
              last_seen_at: addr.geocodedAt,
            } : null;
            const source = (mapSource === 'address' || mapSource === 'both') && !addrMarker ? 'device' : mapSource;
            const markers = source === 'address' ? [addrMarker]
              : source === 'both' ? [device, addrMarker]
              : [device];
            const center = source === 'address'
              ? [addr.lat, addr.lng]
              : [Number(device.latitude), Number(device.longitude)];
            return (
              <Section title="Device Location" actions={
                addrMarker ? (
                  <div className="flex gap-1">
                    {['device', 'address', 'both'].map((s) => (
                      <button key={s} onClick={() => setMapSource(s)}
                        className={`px-2.5 py-1 text-xs font-medium rounded-lg transition ${
                          source === s ? 'bg-primary-600 text-white' : 'bg-secondary-100 text-secondary-600 hover:bg-secondary-200'
                        }`}>
                        {s === 'device' ? 'Device' : s === 'address' ? 'Address' : 'Both'}
                      </button>
                    ))}
                  </div>
                ) : (
                  <span className="text-xs text-secondary-400">
                    {Number(device.latitude).toFixed(5)}, {Number(device.longitude).toFixed(5)} · {device.location_accuracy || 'unknown'}
                  </span>
                )
              }>
                {addrMarker && (
                  <div className={`mb-3 text-xs px-3 py-2 rounded-lg ${lc.mismatch ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'}`}>
                    {lc.mismatch
                      ? `Device is ~${(lc.distanceMeters / 1609.34).toFixed(1)} mi from the registered address (threshold ${(lc.thresholdMeters / 1609.34).toFixed(1)} mi).`
                      : `Device is within ${(lc.distanceMeters / 1609.34).toFixed(1)} mi of the registered address.`}
                  </div>
                )}
                <div style={{ height: 320 }} className="rounded-lg overflow-hidden">
                  <Suspense fallback={<div className="flex items-center justify-center h-full text-secondary-400">Loading map...</div>}>
                    <MapView devices={markers.filter(Boolean)} center={center} />
                  </Suspense>
                </div>
                <div className="mt-2 flex flex-wrap gap-4 text-xs text-secondary-500">
                  <span><span className="inline-block w-2.5 h-2.5 rounded-full bg-emerald-500 mr-1 align-middle" />Device-reported ({device.location_accuracy || 'unknown'})</span>
                  {addrMarker && <span><span className="inline-block w-2.5 h-2.5 rounded-full bg-amber-500 mr-1 align-middle" />Registered address</span>}
                </div>
              </Section>
            );
          })()}

          {/* Stats summary cards */}
          {telemetryStats && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl shadow-card p-4">
                <p className="text-xs text-secondary-400">Avg WiFi Signal</p>
                <p className="text-lg font-bold text-secondary-900">{telemetryStats.avg_signal != null ? `${Math.round(telemetryStats.avg_signal)} dBm` : '--'}</p>
                <p className="text-[11px] text-secondary-400 mt-1">
                  Range: {telemetryStats.min_signal ?? '--'} to {telemetryStats.max_signal ?? '--'} dBm
                </p>
              </div>
              <div className="bg-white rounded-xl shadow-card p-4">
                <p className="text-xs text-secondary-400">Uptime</p>
                <p className="text-lg font-bold text-secondary-900">{telemetryStats.uptimePercent ?? '--'}%</p>
                <p className="text-[11px] text-secondary-400 mt-1">{telemetryStats.total_records} heartbeats / {telemetryStats.periodDays}d</p>
              </div>
              <div className="bg-white rounded-xl shadow-card p-4">
                <p className="text-xs text-secondary-400">Power Source</p>
                {telemetryStats.powerBreakdown?.length > 0 ? (
                  <div className="mt-1 space-y-0.5">
                    {telemetryStats.powerBreakdown.map(p => (
                      <p key={p.power_source} className="text-sm font-medium text-secondary-800">
                        {p.power_source || 'unknown'}: {p.count}
                      </p>
                    ))}
                  </div>
                ) : <p className="text-lg font-bold text-secondary-900">--</p>}
              </div>
              <div className="bg-white rounded-xl shadow-card p-4">
                <p className="text-xs text-secondary-400">Last Known Position</p>
                {telemetryStats.position?.latitude ? (
                  <>
                    <p className="text-sm font-bold text-secondary-900 mt-1">
                      {Number(telemetryStats.position.latitude).toFixed(5)}, {Number(telemetryStats.position.longitude).toFixed(5)}
                    </p>
                    <p className="text-[11px] text-secondary-400">
                      {telemetryStats.position.location_accuracy} &mdash; {telemetryStats.position.location_updated_at ? formatDateTime(telemetryStats.position.location_updated_at) : ''}
                    </p>
                  </>
                ) : <p className="text-lg font-bold text-secondary-900">--</p>}
              </div>
            </div>
          )}

          {chartData.length > 0 ? (
            <>
              <Section title="WiFi Signal Strength">
                <div style={{ height: 250 }}>
                  <ResponsiveContainer>
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="time" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                      <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
                      <Tooltip />
                      <Line type="monotone" dataKey="signal" stroke="#06b6d4" strokeWidth={2} dot={false} name="Signal dBm" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </Section>

              <Section title="Uptime (hours)">
                <div style={{ height: 200 }}>
                  <ResponsiveContainer>
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="time" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                      <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
                      <Tooltip />
                      <Line type="monotone" dataKey="uptime" stroke="#8b5cf6" strokeWidth={2} dot={false} name="Uptime (hrs)" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </Section>

              <Section title="Recent Telemetry Records" actions={
                <span className="text-xs text-secondary-400">Last {telemetry.length} heartbeats</span>
              }>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-secondary-50 text-secondary-600">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-xs">Timestamp</th>
                        <th className="px-3 py-2 text-left font-medium text-xs">WiFi</th>
                        <th className="px-3 py-2 text-left font-medium text-xs">Uptime</th>
                        <th className="px-3 py-2 text-left font-medium text-xs">Power</th>
                        <th className="px-3 py-2 text-left font-medium text-xs">Firmware</th>
                        <th className="px-3 py-2 text-left font-medium text-xs">Config</th>
                        <th className="px-3 py-2 text-left font-medium text-xs">Vacation</th>
                        <th className="px-3 py-2 text-left font-medium text-xs">Position</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-secondary-100">
                      {telemetry.slice(0, 50).map((t, i) => {
                        const extra = t.additional_data ? (typeof t.additional_data === 'string' ? JSON.parse(t.additional_data) : t.additional_data) : {};
                        return (
                          <tr key={t.id || i} className="hover:bg-secondary-50">
                            <td className="px-3 py-1.5 text-xs whitespace-nowrap">{formatDateTime(t.timestamp)}</td>
                            <td className="px-3 py-1.5 text-xs font-mono">{t.wifi_signal_strength != null ? `${t.wifi_signal_strength} dBm` : '--'}</td>
                            <td className="px-3 py-1.5 text-xs font-mono">{t.uptime_seconds != null ? `${Math.round(t.uptime_seconds / 60)}m` : '--'}</td>
                            <td className="px-3 py-1.5 text-xs">
                              <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                t.power_source === 'usb' ? 'bg-emerald-50 text-emerald-700'
                                  : t.power_source === 'battery' ? 'bg-amber-50 text-amber-700'
                                  : 'bg-secondary-100 text-secondary-500'
                              }`}>{t.power_source || '--'}{extra.battery_level != null ? ` ${extra.battery_level}%` : ''}</span>
                            </td>
                            <td className="px-3 py-1.5 text-xs font-mono">{t.firmware_version || '--'}</td>
                            <td className="px-3 py-1.5 text-xs font-mono">{t.config_version ?? '--'}</td>
                            <td className="px-3 py-1.5 text-xs">{t.vacation_mode ? 'On' : 'Off'}</td>
                            <td className="px-3 py-1.5 text-xs font-mono">
                              {extra.latitude ? `${Number(extra.latitude).toFixed(4)}, ${Number(extra.longitude).toFixed(4)}` : '--'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Section>
            </>
          ) : <p className="text-secondary-400">No telemetry data available</p>}
        </div>
      )}

      {activeTab === 'buttons' && (
        <Section title="Button Press Log">
          {buttonLog.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-secondary-50 text-secondary-600">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium">Pressed At</th>
                    <th className="px-4 py-2 text-left font-medium">Duration</th>
                    <th className="px-4 py-2 text-left font-medium">Outcome</th>
                    <th className="px-4 py-2 text-left font-medium">Detail</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-secondary-100">
                  {buttonLog.map(b => (
                    <tr key={b.id} className="hover:bg-secondary-50">
                      <td className="px-4 py-2 text-xs">{formatDateTime(b.pressed_at)}</td>
                      <td className="px-4 py-2 text-xs">{b.duration_seconds != null ? `${b.duration_seconds}s` : '--'}</td>
                      <td className="px-4 py-2">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          b.outcome === 'checkin_clear' ? 'bg-emerald-50 text-emerald-700'
                            : b.outcome === 'emergency' ? 'bg-red-50 text-red-700'
                            : 'bg-secondary-100 text-secondary-600'
                        }`}>{b.outcome}</span>
                      </td>
                      <td className="px-4 py-2 text-xs text-secondary-600 max-w-xs truncate" title={b.detail || ''}>{b.detail || '--'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <p className="text-sm text-secondary-400">No button presses recorded</p>}
        </Section>
      )}

      {activeTab === 'history' && (
        <Section title="Config Version History">
          {configHistory.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-secondary-50 text-secondary-600">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium">Version</th>
                    <th className="px-4 py-2 text-left font-medium">Published By</th>
                    <th className="px-4 py-2 text-left font-medium">Status</th>
                    <th className="px-4 py-2 text-left font-medium">Published</th>
                    <th className="px-4 py-2 text-left font-medium">Applied</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-secondary-100">
                  {configHistory.map(h => (
                    <tr key={h.version} className="hover:bg-secondary-50">
                      <td className="px-4 py-2 font-mono text-xs">v{h.version}</td>
                      <td className="px-4 py-2 text-xs">{h.published_by_email || '--'}</td>
                      <td className="px-4 py-2">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          h.apply_status === 'applied' ? 'bg-emerald-50 text-emerald-700'
                            : h.apply_status === 'failed' ? 'bg-red-50 text-red-700'
                            : 'bg-amber-50 text-amber-700'
                        }`}>{h.apply_status || 'pending'}</span>
                      </td>
                      <td className="px-4 py-2 text-xs">{formatDateTime(h.created_at)}</td>
                      <td className="px-4 py-2 text-xs">{h.applied_at ? formatDateTime(h.applied_at) : '--'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <p className="text-sm text-secondary-400">No config history</p>}
        </Section>
      )}

      {activeTab === 'message' && (
        <Section title="Audio Message">
          <div className="space-y-4">
            <p className="text-sm text-secondary-500">
              Send a spoken message to the patient's device. The SMC turns it into speech; the device
              downloads it and <strong>loops it aloud</strong> until the patient clears it with the button —
              or you stop it here. Sending a new message replaces any message already playing.
            </p>

            {messageStatus && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-secondary-400">Status</span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                  messageStatus === 'playing' ? 'bg-emerald-50 text-emerald-700'
                    : messageStatus === 'queued' ? 'bg-amber-50 text-amber-700'
                    : 'bg-secondary-100 text-secondary-600'
                }`}>
                  {messageStatus === 'playing' ? 'Playing on device'
                    : messageStatus === 'queued' ? 'Queued — waiting for device'
                    : 'Cleared'}
                </span>
                {activeMessageId && <span className="text-xs text-secondary-400">#{activeMessageId}</span>}
              </div>
            )}

            <div>
              <label className="block text-xs text-secondary-500 mb-1">Message</label>
              <textarea
                value={messageText}
                onChange={e => setMessageText(e.target.value)}
                maxLength={500}
                placeholder="e.g. Grandma, please remember to take your afternoon medicine."
                className="w-full px-3 py-2 border border-secondary-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none"
                rows={3} />
              <p className="text-xs text-secondary-400 mt-1">{messageText.length}/500</p>
            </div>

            {messageErr && <p className="text-xs text-red-600">{messageErr}</p>}
            {messageInfo && <p className="text-xs text-emerald-600">{messageInfo}</p>}

            <div className="flex gap-2 pt-1">
              <button onClick={sendCustomMessage} disabled={messageSending || !messageText.trim()}
                className="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50">
                {messageSending ? 'Working…' : 'Send Message'}
              </button>
              {activeMessageId && messageStatus !== 'cleared' && (
                <button onClick={clearCustomMessage} disabled={messageSending}
                  className="px-4 py-2 bg-red-50 text-red-700 text-sm font-medium rounded-lg hover:bg-red-100 disabled:opacity-50">
                  {messageSending ? 'Working…' : 'Stop Message'}
                </button>
              )}
            </div>
          </div>
        </Section>
      )}
    </div>
  );
}
